import React, { Fragment } from 'react'
import { Row, Col, FormGroup, FormControl, ControlLabel, Grid } from 'react-bootstrap'
import { RegionDropdown, CountryDropdown } from 'react-country-region-selector'
import { byCountry } from 'country-code-lookup'
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import propTypeSchema from 'react-json-schema-proptypes'
import Radium from 'radium'
import { Address, BasicDonorInfo, Card, Typography } from '../types'
import { valueOrUndefined } from '../common'
import Phone from 'phone'

const schema = {
  'type': 'object',
  'properties': {
    'type': {'enum': ['beta']},
    'firstNameLabel': {'type': 'string'},
    'lastNameLabel': {'type': 'string'},
    'emailLabel': {'type': 'string'},
    'addressLabel': {'type': 'string'},
    'cityLabel': {'type': 'string'},
    'stateLabel': {'type': 'string'},
    'zipLabel': {'type': 'string'},
    'countryLabel': {'type': 'string'},
    'mobileLabel': {'type': 'string'},
    'headerBackgroundColor': {'type': 'string'},
    'headerLabel': {'type': 'string'},
    'header': Typography.Schema,
    'mobileBreakPoint': {'type': 'number'},
    'mobileMaxWidth': {'type': 'number'},
    'astStyle': Typography.Schema,
    'labelStyle': Typography.Schema,
    'formStyle': Typography.Schema,
    'mobileRequired': {'type': 'boolean'},
  }
}

const headerBackgroundColor = '#E61844'

@Radium
export default class BetaDonorInfo extends React.PureComponent {
  static propSchema = schema

  static propTypes = propTypeSchema(schema, {
    'required': ['onInfoChanged', 'onValidChanged', 'onAddressChanged'],
    'properties': {
      'values': BasicDonorInfo.Schema,
      'address': Address.Schema,
      'displayErrors': {'type': 'boolean'},
      'onInfoChanged': {'isFunction': true},
      'onValidChanged': {'isFunction': true},
      'onAddressChanged': {'isFunction': true},
      'cards': {'type': 'array', 'items': Card.Schema},
      'selectedCard': Card.Schema
    }
  })

  static defaultProps = {
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Email Address',
    addressLabel: 'Address Line',
    cityLabel: 'City',
    stateLabel: 'State/Province',
    zipLabel: 'Zip/Postal Code',
    headerLabel: '2. Billing Information',
    countryLabel: 'Country',
    mobileNumberLabel: 'Mobile Number',
    headerBackgroundColor: headerBackgroundColor,
    mobileBreakPoint: 992,
    mobileMaxWidth: 560,
    mobileRequired: false,
  }

  constructor (props) {
    super(props)
    this.state = {
      formErrors: {
        firstName: 'Required',
        lastName: 'Required',
        email: 'Required',
        address: 'Required',
        city: 'Required',
        zipCode: 'Required',
        state: 'Required',
        mobileNumber: (this.props.mobileRequired === "true") ? 'Required' : null,
      }
    }
  }

  componentDidMount() {
    this.validateField('firstName', valueOrUndefined(this.props, `values.firstName`))
    this.validateField('lastName', valueOrUndefined(this.props, `values.lastName`))
    this.validateField('email', valueOrUndefined(this.props, `values.email`))
    this.validateField('mobileNumber', valueOrUndefined(this.props, `values.mobileNumber`))
    this.props.onValidChanged(this.allValid())
  }

  handleUserInput = (e) => {
    const name = e.target.name
    const value = e.target.value
    this.props.onInfoChanged({[name]: value})
  }

  handleAddressChange = (e) => {
    const name = e.target.name
    const value = e.target.value
    this.props.onAddressChanged({[name]: value})
  }

  handleRegionChange = (val) => {
    this.props.onAddressChanged({['state']: val})
  }

  handleCountryChange = (val) => {
    this.props.onAddressChanged({['country']: val})
  }

  validateField = (fieldName, value) => {
    let zipCodeError = (zipCode, country) => {
      if (!zipCode) return 'Required'
      let countryCode = byCountry(country) ? byCountry(country).iso2 : null
      let validatorExists = countryCode && postcodeValidatorExistsForCountry(countryCode)
      return (!validatorExists || postcodeValidator(zipCode, countryCode)) ? null : "Invalid Zip Code"
    }
    const fieldValidationErrors = {}
    switch (fieldName) {
      case 'email':
        fieldValidationErrors.email = value && value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i) ? null : 'Not a valid email'
        break
      case 'firstName':
        fieldValidationErrors.firstName = value && value.length > 0 ? null : 'Required'
        break
      case 'lastName':
        fieldValidationErrors.lastName = value && value.length > 0 ? null : 'Required'
        break
      case 'mobileNumber':
        if (this.props.mobileRequired === "true") {
          fieldValidationErrors.mobileNumber = (!value || value.length === 0) ? 'Required' : Phone(value)[0] ? null : 'Not a valid phone number'
        } else {
          fieldValidationErrors.phoneNumber = !value || value.length === 0 || Phone(value)[0] ? null : 'Not a valid phone number'
        }
        break
      case 'street':
        fieldValidationErrors.address = value && value.length > 0 ? null : 'Required'
        break
      case 'city':
        fieldValidationErrors.city = value && value.length > 0 ? null : 'Required'
        break
      case 'zipCode':
        fieldValidationErrors.zipCode = zipCodeError(value, this.props.address.country)
        break
      case 'country':
        fieldValidationErrors.country = value && value.length > 0 ? null : 'Required'
        fieldValidationErrors.zipCode = zipCodeError(this.props.address.zipCode, value)
        break
      case 'state':
        fieldValidationErrors.state = value && value.length > 0 ? null : 'Required'
        break
      default:
        break
    }

    this.setState(state => {
      return {
        formErrors: {
        ...state.formErrors,
        ...fieldValidationErrors
        }
      }
    }, () => this.props.onValidChanged(this.allValid()))
  }

  allValid = () => {
    return (!this.state.formErrors.email && !this.state.formErrors.firstName && !this.state.formErrors.lastName && !this.state.formErrors.email &&
      !this.state.formErrors.address && !this.state.formErrors.city && !this.state.formErrors.state && !this.state.formErrors.zipCode && !this.state.formErrors.country && !this.state.formErrors.mobileNumber)
      || !!this.props.selectedCard
  }

  checkValidateField = (prevProps, fieldName, mapName) => {
    if(valueOrUndefined(this.props, `${mapName}.${fieldName}`) !== valueOrUndefined(prevProps, `${mapName}.${fieldName}`)) {
      this.validateField(fieldName, valueOrUndefined(this.props, `${mapName}.${fieldName}`))
    }
  }

  componentDidUpdate (prevProps) {
    if(this.props.values !== prevProps.values) {
      this.checkValidateField(prevProps, 'firstName', 'values')
      this.checkValidateField(prevProps, 'lastName', 'values')
      this.checkValidateField(prevProps, 'email', 'values')
      this.checkValidateField(prevProps, 'mobileNumber', 'values')
    }
    if (this.props.address !== prevProps.address) {
      this.checkValidateField(prevProps, 'street', 'address')
      this.checkValidateField(prevProps, 'zipCode', 'address')
      this.checkValidateField(prevProps, 'city', 'address')
      this.checkValidateField(prevProps, 'state', 'address')
      this.checkValidateField(prevProps, 'country', 'address')
    }
  }

  render () {
    const styles = {
      headerBackgroundColor: {},
      header: valueOrUndefined(this.props, 'header'),
      breakPointStyle: {}
    }
    if (this.props.mobileMaxWidth) {
      styles['breakPointStyle'][`@media (max-width: ${this.props.mobileBreakPoint - 1}px)`] = {
        maxWidth: this.props.mobileMaxWidth
      }
    }
    if (this.props.headerBackgroundColor) {
      styles['headerBackgroundColor'] = {
        backgroundColor: this.props.headerBackgroundColor
      }
    }
    return (
      <div className='beta container-fluid' style={[styles.breakPointStyle]}>
        {this.props.cards && (this.props.cards.length === 0 || !this.props.selectedCard) &&
        <Row>
          <div className={'headers'} style={[styles.headerBackgroundColor, styles.header]}>
            {this.props.headerLabel}
          </div>
        </Row>
        }
        {this.props.cards && this.props.cards.length === 0 &&
        <Fragment>
          <Row className={'rowPadding'}>
            <Col xs={12} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.firstName ? ' has-error' : '')}
                controlId='firstName'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.firstNameLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.firstName &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.firstName}</div>
                }
                <FormControl
                  name='firstName'
                  id='firstName'
                  type='text'
                  value={valueOrUndefined(this.props, 'values.firstName')}
                  onChange={this.handleUserInput}
                  style={this.props.formStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className={'rowPadding'}>
            <Col xs={12} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.lastName ? ' has-error' : '')}
                controlId='lastName'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.lastNameLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.lastName &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.lastName}</div>
                }
                <FormControl
                  name='lastName'
                  id='lastName'
                  type='text'
                  value={valueOrUndefined(this.props, 'values.lastName')}
                  onChange={this.handleUserInput}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
          </Row>
          <Row className={'rowPadding'}>
            <Col xs={12} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.email ? ' has-error' : '')}
                controlId='email'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.emailLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.email &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.email}</div>
                }
                <FormControl
                  name='email'
                  id='email'
                  type='text'
                  value={valueOrUndefined(this.props, 'values.email')}
                  onChange={this.handleUserInput}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
          </Row>
        </Fragment>
        }
        {this.props.cards && (this.props.cards.length === 0 || !this.props.selectedCard) &&
        <Fragment>
          <Row className={'rowPadding'}>
            <Col xs={12} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.address ? ' has-error' : '')}
                controlId='street'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.addressLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.address &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.address}</div>
                }
                <FormControl
                  name='street'
                  id='street'
                  type='text'
                  value={valueOrUndefined(this.props, 'address.street')}
                  onChange={this.handleAddressChange}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
          </Row>
          <Row className={'rowPadding'}>
            <Col xs={6} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.city ? ' has-error' : '')}
                controlId='city'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.cityLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.city &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.city}</div>
                }
                <FormControl
                  name='city'
                  id='city'
                  type='text'
                  value={valueOrUndefined(this.props, 'address.city')}
                  onChange={this.handleAddressChange}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
            <Col xs={6} className={'colPadding'}>
              <FormGroup
              className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.state ? ' has-error' : '')}>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.stateLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.state &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.state}</div>
                }
                <RegionDropdown
                  id={'state'}
                  name={'state'}
                  country={valueOrUndefined(this.props, 'address.country')}
                  value={valueOrUndefined(this.props, 'address.state')}
                  onChange={this.handleRegionChange}
                  showDefaultOption={true}
                  style={this.props.formStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className={'rowPadding'}>
            <Col xs={6} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.zipCode ? ' has-error' : '')}
                controlId='zipCode'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.zipLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.zipCode &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.zipCode}</div>
                }
                <FormControl
                  name='zipCode'
                  id='zipCode'
                  type='text'
                  value={valueOrUndefined(this.props, 'address.zipCode')}
                  onChange={this.handleAddressChange}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
            <Col xs={6} className={'colPadding'}>
              <FormGroup
                className={'infoEntry' + (this.props.displayErrors && this.state.formErrors.country ? ' has-error' : '')}>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.countryLabel}
                  <span className={'astStyle'} style={this.props.astStyle}> *</span>
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.country &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.country}</div>
                }
                <CountryDropdown
                  id={'country'}
                  name={'country'}
                  value={valueOrUndefined(this.props, 'address.country')}
                  onChange={this.handleCountryChange}
                  showDefaultOption={false}
                  style={this.props.formStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className={'rowPadding'}>
            <Col xs={12} className={'colPadding'}>
              <FormGroup
                className={'infoEntry last' + (this.props.displayErrors && this.state.formErrors.mobileNumber ? ' has-error' : '')}
                controlId='mobileNumber'>
                <ControlLabel style={this.props.labelStyle}>
                  {this.props.mobileNumberLabel} { this.props.mobileRequired === "true" ? <span className={'astStyle'} style={this.props.astStyle}> *</span> : "" }
                </ControlLabel>
                {this.props.displayErrors && this.state.formErrors.mobileNumber &&
                <div className='formError' style={this.props.labelStyle}>&nbsp;{this.state.formErrors.mobileNumber}</div>
                }
                <FormControl
                  name='mobileNumber'
                  id='mobileNumber'
                  type='text'
                  value={valueOrUndefined(this.props, 'values.mobileNumber')}
                  onChange={this.handleUserInput}
                  style={this.props.formStyle} />
              </FormGroup>
            </Col>
          </Row>
        </Fragment>
        }
      </div>
    )
  }
}
